import { useState, useEffect, useCallback } from "react"
import { useParams } from "react-router-dom"
import { getDevice } from "@src/api"
import { SLIM_DEVICE_TYPE } from "@src/devices/types"
import { usePartner } from "@src/partner"
import { useFleets } from "@src/settings"
import { useDevice } from "../useDevice"
import { DeviceFilters } from "./types"

type UseDeviceListParams = {
  activePage: number
  sorting: boolean
  sortHeader: string
  devicesLength: number
  filters: DeviceFilters
  searchParams: URLSearchParams
}

export const useDeviceList = ({
  activePage,
  sorting,
  sortHeader,
  devicesLength,
  filters,
  searchParams,
}: UseDeviceListParams) => {
  const { partnerUuid } = useParams()
  const { partner } = usePartner()
  const { fleet } = useFleets()
  const { clearDevice, clearLastTrips } = useDevice()

  const [devices, setDevices] = useState<SLIM_DEVICE_TYPE[]>([])
  const [pagesCount, setPagesCount] = useState<number>(1)
  const [loading, setLoading] = useState<boolean>(true)
  const [headerLoad, setHeaderLoad] = useState<boolean>(false)

  const listDevices = useCallback(() => {
    const effectiveUuid = partner?.uuid || partnerUuid
    if (!effectiveUuid) return

    getDevice(effectiveUuid, fleet && fleet.uuid, {
      page: activePage,
      per_page: devicesLength,
      sort_desc: sorting,
      sort_by: sortHeader,
      ...filters,
    }).then(({ response, data }) => {
      if (response.ok) {
        setDevices(data.devices)
        setPagesCount(Math.ceil(data.meta.total / data.meta.per_page))
      }
      setLoading(false)
      setHeaderLoad(false)
    })
  }, [partner?.uuid, partnerUuid, fleet, activePage, devicesLength, sorting, sortHeader, filters])

  useEffect(() => {
    setLoading(true)
    listDevices()
    clearDevice()
    clearLastTrips()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sorting, sortHeader, activePage, fleet, devicesLength, searchParams.toString()])

  return {
    devices,
    pagesCount,
    loading,
    headerLoad,
    setLoading,
    setHeaderLoad,
  }
}
